import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  History,
  IndianRupee,
  RefreshCw,
  Search,
  Target,
  User,
} from 'lucide-react';

import api from '../../helpers/api_helper';

type StarlineBetRecord = {
  id: number;
  user: number | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  mobile_number?: string | null;
  app?: string | null;
  market: number | null;
  market_name?: string | null;
  session: number | null;
  session_date?: string | null;
  session_time?: string | null;
  session_result?: string | null;
  bet_type: number | null;
  bet_type_name?: string | null;
  app_bet_type: number | null;
  bet_code?: string | null;
  selected_number?: string | null;
  amount?: string | number | null;
  payout_multiplier?: string | number | null;
  potential_payout?: string | number | null;
  winning_amount?: string | number | null;
  before_amount?: string | number | null;
  after_amount?: string | number | null;
  status?: string | null;
  result?: Record<string, unknown> | null;
  settled_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type StarlineHistoryResponse = {
  success?: boolean;
  count?: number;
  bets?: StarlineBetRecord[];
  message?: string;
  detail?: string;
};

type FilterState = {
  search: string;
  status: string;
  bet_code: string;
  date_from: string;
  date_to: string;
};

const initialFilters: FilterState = {
  search: '',
  status: '',
  bet_code: '',
  date_from: '',
  date_to: '',
};

const StarlineBetHistory = () => {
  const [bets, setBets] = useState<StarlineBetRecord[]>([]);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<FilterState>(initialFilters);
  const [expandedBetId, setExpandedBetId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const queryParams = new URLSearchParams();

      if (appliedFilters.search.trim()) {
        queryParams.append('search', appliedFilters.search.trim());
      }

      if (appliedFilters.status) {
        queryParams.append('status', appliedFilters.status);
      }

      if (appliedFilters.bet_code) {
        queryParams.append('bet_code', appliedFilters.bet_code);
      }

      if (appliedFilters.date_from) {
        queryParams.append('date_from', appliedFilters.date_from);
      }

      if (appliedFilters.date_to) {
        queryParams.append('date_to', appliedFilters.date_to);
      }

      const query = queryParams.toString();
      const url = `/starline/history/${query ? `?${query}` : ''}`;

      const response = await api.get<StarlineHistoryResponse>(url);
      const responseBets = Array.isArray(response.data?.bets)
        ? response.data.bets
        : [];

      setBets(responseBets);
    } catch (requestError: any) {
      console.error('Failed to load Starline bid history:', requestError);

      const responseData = requestError?.response?.data;
      const message =
        responseData?.error ||
        responseData?.message ||
        responseData?.detail ||
        requestError?.message ||
        'Unable to load Starline bid history.';

      setError(String(message));
      setBets([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const summary = useMemo(() => {
    return bets.reduce(
      (current, bet) => {
        const amount = Number(bet.amount || 0);
        const payout = Number(bet.winning_amount || 0);
        const status = String(bet.status || '').toLowerCase();

        current.totalBets += 1;
        current.totalWagered += amount;
        current.totalWon += payout;

        if (status === 'won') current.won += 1;
        if (status === 'lost') current.lost += 1;
        if (status === 'pending') current.pending += 1;

        return current;
      },
      {
        totalBets: 0,
        totalWagered: 0,
        totalWon: 0,
        won: 0,
        lost: 0,
        pending: 0,
      },
    );
  }, [bets]);

  const formatCurrency = (
    value: string | number | null | undefined,
  ) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSessionTime = (value?: string | null) => {
    if (!value) return '—';

    const [hoursText, minutesText] = value.split(':');
    const hours = Number(hoursText);
    const minutes = Number(minutesText);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return value;
    }

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    return date
      .toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .toUpperCase();
  };

  const formatBetCode = (value?: string | null) => {
    if (!value) return '—';

    return value
      .split('_')
      .map(
        (part) =>
          part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
      )
      .join(' ');
  };

  const getStatusClass = (status?: string | null) => {
    switch (String(status || '').toLowerCase()) {
      case 'won':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setExpandedBetId(null);
    setAppliedFilters(filters);
  };

  const handleClear = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setExpandedBetId(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <History className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
            Starline User Bid History
          </h1>
        </div>
        <p className="text-sm text-slate-600 md:text-base">
          Review Starline bets, selected numbers, wallet balances and
          settlement status.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Bets</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">
                {summary.totalBets}
              </p>
            </div>
            <History className="h-7 w-7 text-blue-600" />
          </div>
        </div>

        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Wagered</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">
                {formatCurrency(summary.totalWagered)}
              </p>
            </div>
            <IndianRupee className="h-7 w-7 text-purple-600" />
          </div>
        </div>

        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Pending Bets</p>
              <p className="mt-1 text-2xl font-bold text-yellow-700">
                {summary.pending}
              </p>
            </div>
            <Target className="h-7 w-7 text-yellow-600" />
          </div>
        </div>

        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Won</p>
              <p className="mt-1 text-2xl font-bold text-green-700">
                {formatCurrency(summary.totalWon)}
              </p>
            </div>
            <IndianRupee className="h-7 w-7 text-green-600" />
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSearchSubmit}
        className="mb-6 rounded-xl border bg-white p-4 shadow-sm"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
          <div className="relative xl:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Search number, market or mobile"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={filters.status}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                status: event.target.value,
              }))
            }
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filters.bet_code}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                bet_code: event.target.value,
              }))
            }
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            <option value="">All bet types</option>
            <option value="single_digit">Single Digit</option>
            <option value="single_pana">Single Pana</option>
            <option value="double_pana">Double Pana</option>
            <option value="triple_pana">Triple Pana</option>
          </select>

          <input
            type="date"
            value={filters.date_from}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                date_from: event.target.value,
              }))
            }
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
          />

          <input
            type="date"
            value={filters.date_to}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                date_to: event.target.value,
              }))
            }
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="submit"
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Apply Filters
          </button>

          <button
            type="button"
            onClick={handleClear}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Clear
          </button>

          <button
            type="button"
            onClick={fetchHistory}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-12 px-4 py-3" />
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Session
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Bet Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Selected Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Potential Payout
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Created
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-6 py-12 text-center text-slate-500"
                  >
                    Loading Starline bid history...
                  </td>
                </tr>
              ) : bets.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-6 py-12 text-center"
                  >
                    <History className="mx-auto mb-3 h-11 w-11 text-slate-300" />
                    <p className="font-medium text-slate-600">
                      No Starline bid history found
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Try changing the search or filter criteria.
                    </p>
                  </td>
                </tr>
              ) : (
                bets.map((bet) => {
                  const expanded = expandedBetId === bet.id;

                  return (
                    <React.Fragment key={bet.id}>
                      <tr className="hover:bg-slate-50">
                        <td className="px-4 py-4">
                          <button
                            type="button"
                            aria-label={
                              expanded
                                ? 'Collapse bet details'
                                : 'Expand bet details'
                            }
                            onClick={() =>
                              setExpandedBetId(expanded ? null : bet.id)
                            }
                            className="rounded p-1 text-slate-500 hover:bg-slate-100"
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-900">
                          {bet.id}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {bet.first_name ||
                                  bet.username ||
                                  `User #${bet.user ?? '—'}`}
                              </p>
                              <p className="text-xs text-slate-500">
                                {bet.mobile_number || '—'}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <p className="text-sm font-medium text-slate-900">
                            {formatSessionTime(bet.session_time)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {bet.session_date || '—'}
                          </p>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <p className="text-sm text-slate-900">
                            {bet.bet_type_name ||
                              formatBetCode(bet.bet_code)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {Number(bet.payout_multiplier || 0)}x
                          </p>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm font-semibold text-slate-900">
                          {bet.selected_number || '—'}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-900">
                          {formatCurrency(bet.amount)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-900">
                          {formatCurrency(bet.potential_payout)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${getStatusClass(
                              bet.status,
                            )}`}
                          >
                            {bet.status || 'unknown'}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-500">
                          {formatDateTime(bet.created_at)}
                        </td>
                      </tr>

                      {expanded && (
                        <tr className="bg-slate-50">
                          <td colSpan={10} className="px-6 py-5">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border bg-white p-4">
                                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                                  Bet Details
                                </h3>
                                <div className="space-y-2 text-sm">
                                  <DetailRow
                                    label="Market"
                                    value={bet.market_name || 'Starline'}
                                  />
                                  <DetailRow
                                    label="Session ID"
                                    value={bet.session ?? '—'}
                                  />
                                  <DetailRow
                                    label="Bet Code"
                                    value={formatBetCode(bet.bet_code)}
                                  />
                                  <DetailRow
                                    label="Selected Number"
                                    value={bet.selected_number || '—'}
                                  />
                                </div>
                              </div>

                              <div className="rounded-lg border bg-white p-4">
                                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                                  Wallet Details
                                </h3>
                                <div className="space-y-2 text-sm">
                                  <DetailRow
                                    label="Before Bet"
                                    value={formatCurrency(bet.before_amount)}
                                  />
                                  <DetailRow
                                    label="After Bet"
                                    value={formatCurrency(bet.after_amount)}
                                  />
                                  <DetailRow
                                    label="Winning Amount"
                                    value={formatCurrency(bet.winning_amount)}
                                  />
                                  <DetailRow
                                    label="Potential Payout"
                                    value={formatCurrency(
                                      bet.potential_payout,
                                    )}
                                  />
                                </div>
                              </div>

                              <div className="rounded-lg border bg-white p-4">
                                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                                  Result
                                </h3>
                                <div className="space-y-2 text-sm">
                                  <DetailRow
                                    label="Session Result"
                                    value={
                                      bet.session_result || 'Not declared'
                                    }
                                  />
                                  <DetailRow
                                    label="Status"
                                    value={bet.status || 'Unknown'}
                                  />
                                  <DetailRow
                                    label="Settled At"
                                    value={formatDateTime(bet.settled_at)}
                                  />
                                </div>
                              </div>

                              <div className="rounded-lg border bg-white p-4">
                                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                                  Timestamps
                                </h3>
                                <div className="space-y-2 text-sm">
                                  <DetailRow
                                    label="Created"
                                    value={formatDateTime(bet.created_at)}
                                  />
                                  <DetailRow
                                    label="Updated"
                                    value={formatDateTime(bet.updated_at)}
                                  />
                                  <DetailRow
                                    label="App Bet Type"
                                    value={bet.app_bet_type ?? '—'}
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-medium text-slate-900">
      {value}
    </span>
  </div>
);

export default StarlineBetHistory;
